import {Inject, Injectable} from '@angular/core';
import tinycolor from 'tinycolor2';
import {DOCUMENT} from '@angular/common';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Injectable()
export class ThemeService {

  constructor(@Inject(DOCUMENT) private _document: Document) {
  }

  public setApplicationTheme(primaryColor: string, secondaryColor: string, warnColor: string, tagName?: string, className?: string): void {
    this._saveColor('primary', this._computeColors(primaryColor), tagName, className);
    this._saveColor('secondary', this._computeColors(secondaryColor), tagName, className);
    this._saveColor('warn', this._computeColors(warnColor), tagName, className);
  }

  public getThemeColor(appSelector: string, identifier: string): string {
    const globalColor = getComputedStyle(this._document.documentElement).getPropertyValue(identifier);
    if (this._document.getElementsByTagName(appSelector)[0]) {
      return getComputedStyle(this._document.getElementsByTagName(appSelector)[0]).getPropertyValue(identifier) || globalColor;
    }
    return globalColor;
  }

  private _saveColor(identifier: string, palette: Color[], tagName?: string, className?: string): void {
    for (const color of palette) {
      if (color.name === '500') {
        this._document.documentElement.style.setProperty(`--lv-core-color-${identifier}`, color.hex);
      }
      const key1 = `--lv-core-color-${identifier}-${color.name}`;
      const value1 = color.hex;
      const key2 = `--lv-core-color-${identifier}-contrast-${color.name}`;
      const value2 = color.darkContrast ? 'var(--lv-core-color-text-dark)' : 'var(--lv-core-color-text-light)';
      if (!!tagName) {
        const appRoots = this._document.getElementsByTagName(tagName) as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < appRoots.length; i++) {
          const appRoot = appRoots[i];
          appRoot?.style.setProperty(key1, value1);
          appRoot?.style.setProperty(key2, value2);
        }
      } else if (!!className) {
        const classElement = this._document.getElementsByClassName(className)[0] as HTMLElement;
        classElement?.style.setProperty(key1, value1);
        classElement?.style.setProperty(key2, value2);
      } else {
        this._document.documentElement.style.setProperty(key1, value1);
        this._document.documentElement.style.setProperty(key2, value2);
      }
    }
  }

  private _computeColors(hex: string): Color[] {
    const baseLight = tinycolor('#ffffff');
    const baseDark = this._generateDarkBase(tinycolor(hex).toRgb(), tinycolor(hex).toRgb());
    const baseTriad = tinycolor(hex).tetrad();
    return [
      this._getColorObject(tinycolor.mix(baseLight, hex, 12), '50'),
      this._getColorObject(tinycolor.mix(baseLight, hex, 30), '100'),
      this._getColorObject(tinycolor.mix(baseLight, hex, 50), '200'),
      this._getColorObject(tinycolor.mix(baseLight, hex, 70), '300'),
      this._getColorObject(tinycolor.mix(baseLight, hex, 85), '400'),
      this._getColorObject(tinycolor.mix(baseLight, hex, 100), '500'),
      this._getColorObject(tinycolor.mix(baseDark, hex, 87), '600'),
      this._getColorObject(tinycolor.mix(baseDark, hex, 70), '700'),
      this._getColorObject(tinycolor.mix(baseDark, hex, 54), '800'),
      this._getColorObject(tinycolor.mix(baseDark, hex, 25), '900'),
      this._getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(80).lighten(65), 'A100'),
      this._getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(80).lighten(55), 'A200'),
      this._getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(100).lighten(45), 'A400'),
      this._getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(100).lighten(40), 'A700')
    ];
  }

  private _getColorObject(value, name): Color {
    const c = tinycolor(value);
    return {
      name,
      hex: c.toHexString(),
      darkContrast: c.isLight()
    };
  }

  private _generateDarkBase(rgb1, rgb2) {
    rgb1.b = Math.floor(rgb1.b * rgb2.b / 255);
    rgb1.g = Math.floor(rgb1.g * rgb2.g / 255);
    rgb1.r = Math.floor(rgb1.r * rgb2.r / 255);
    return tinycolor('rgb ' + rgb1.r + ' ' + rgb1.g + ' ' + rgb1.b);
  }
}
